import React from 'react';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { atomDark } from 'react-syntax-highlighter/dist/esm/styles/prism';

interface CodeHighlighterProps {
  code: string;
  language: string;
}

const languageMap: Record<string, string> = {
  bash: 'bash',
  shell: 'bash',
  sh: 'bash',
  zsh: 'bash',
  c: 'c',
  h: 'c',
  cpp: 'cpp',
  'c++': 'cpp',
  cc: 'cpp',
  cxx: 'cpp',
  hpp: 'cpp',
  csharp: 'csharp',
  cs: 'csharp',
  'c#': 'csharp',
  css: 'css',
  go: 'go',
  golang: 'go',
  graphql: 'graphql',
  html: 'html',
  htm: 'html',
  java: 'java',
  javascript: 'javascript',
  js: 'javascript',
  jsx: 'jsx',
  json: 'json',
  kotlin: 'kotlin',
  kt: 'kotlin',
  markdown: 'markdown',
  md: 'markdown',
  php: 'php',
  python: 'python',
  py: 'python',
  r: 'r',
  ruby: 'ruby',
  rb: 'ruby',
  rust: 'rust',
  rs: 'rust',
  sql: 'sql',
  swift: 'swift',
  ts: 'typescript',
  tsx: 'tsx',
  typescript: 'typescript',
  xml: 'xml',
  yaml: 'yaml',
  yml: 'yaml',
};

const mapLanguage = (lang: string): string => {
  const normalized = lang.trim().toLowerCase();
  return languageMap[normalized] || 'text';
};

const CodeHighlighter: React.FC<CodeHighlighterProps> = ({ code, language }) => {
  return (
    <SyntaxHighlighter
      language={mapLanguage(language)}
      style={atomDark}
      // Wrap long lines instead of horizontal-scrolling them. This is
      // worth a small loss of pixel-precise alignment because:
      //  (a) chat responses occasionally render prose as code blocks due
      //      to over-eager detection or LLM wrapping output in fences,
      //      and a horizontal-scrolling paragraph of prose is a UX
      //      disaster (sample: maplept2-reasoning's box-puzzle answer
      //      rendered as a single horizontally-scrolling line),
      //  (b) for genuine code, wrapped long lines are still readable —
      //      worst case the user resizes the chat panel.
      wrapLongLines={true}
      customStyle={{
        margin: 0,
        padding: '1rem',
        borderRadius: 0,
        minHeight: '100%',
        background: 'transparent',
        lineHeight: '1.6',
        overflow: 'visible',
        // Belt-and-braces — wrapLongLines uses pre-wrap on the <pre>
        // element internally, but we set whitespace explicitly so a
        // future SyntaxHighlighter upgrade doesn't silently regress.
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
        overflowWrap: 'anywhere',
      }}
      codeTagProps={{
        style: {
          fontSize: 'inherit',
          fontFamily:
            'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
          overflowWrap: 'anywhere',
        },
      }}
    >
      {code || ' '}
    </SyntaxHighlighter>
  );
};

export default CodeHighlighter;
